import {useEffect, useLayoutEffect, useState} from "react";
import {toast} from "react-toastify";
import {
    GetSingleUserProvider,
    GetSupplierInfoById,
    UpdateSupplierEmployee
} from "../../../../../reduxToolkit/Providers/providersApi";
import {getPhotoPicker, PHOTO_PICKER_MODEL} from '../../../../../components/PhotoPicker/SubComponent/Models/Models';
import {errorMsg} from '../../../../../Apis/functions';
import {EXTRA_DATA, SUPPLIER_EMPLOYEE_DATA, TABS_SUPPLIER_EMPLOYEE} from '../../../../Dashboard/Company/Employees/Model/Models';
import IconPersonDefault from "../../../../../assets/ImagesDefaults/ic-person-v2.svg";
import { useSelector } from "react-redux";
import { GetUserRestriction } from "../../../../../reduxToolkit/restrictions/RestrictionApi";
import { GetHeaders } from './../../../../../reduxToolkit/headers/HeadersApi';
import HandleChange from "../../../../../Helpers/HandleChange";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 */

/**
 * @file SupplierFlowEmployeeUpdateApp.jsx
 * @description Custom hook for managing the creation flow of a supplier employee user.
 *
 * @Email: azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @JobTitle: Full Stack Developer
 */

/**
 * Custom hook to handle the form logic and data flow for creating a supplier employee.
 *
 * @param {function} t - Translation function from i18next.
 * @param {function} dispatch - Redux dispatch function.
 * @param {function} navigate - Function to navigate programmatically.
 * @param {function} setEmptyFields - Setter for emptyFields.
 * @param {boolean} isValidEmail - Flag for valid email input.
 * @param {boolean} isValidPhoneNumber - Flag for valid phone number input.
 * @param {boolean} isValidDob - Flag for valid date of birth input.
 *
 * @param supplierId
 * @param params
 * @param photoPicker
 * @param setPhotoPicker
 * @returns {Object} Hook utilities and state for managing supplier employee creation.
 */

export const SupplierFlowEmployeeUpdateApp = (
    {
        t,
        permission,
        dispatch,
        setEmptyFields,
        isValidEmail,
        isValidPhoneNumber,
        isValidDob,
        isValidPassword,
        supplierId,
        params,
        photoPicker,
        setPhotoPicker,
        setSecretModal,
        setSecretKey,
        extraData,
        extraDataEnabled,
        setExtraData,
        setExtraDataEnabled,
        headersList
    }
) => {
    const [user, setUser] = useState({...SUPPLIER_EMPLOYEE_DATA, supplierId : supplierId });
    // Override companyName from user object
    const companyName = user.supplierCompanyName;


    const [steps, setSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(TABS_SUPPLIER_EMPLOYEE.DETAILS.INDEX);
    const [modified, setModified] = useState(false);
    const [showUnlinkBtn, setShowUnlinkBtn] = useState(false);

    const  STEP_EMPLOYEE_UPDATE = (extraDataEnabled) => {
        if (extraDataEnabled){
            return [t('details'), t('other_details'), t('add_photo')];
        }
        return [t('details'), t('add_photo')];
    }

    /**
     * React layout effect hook that runs when `t` or `permission` change.
     *
     * - Updates the `steps` state with the translated step names.
     * - `t('details')` and `t('photo')` are used to get the corresponding translations.
     */
    useLayoutEffect(() => {
        setSteps(STEP_EMPLOYEE_UPDATE(extraDataEnabled))
    }, [extraDataEnabled, t, permission]);

    /**
     * Fetches supplier information from the backend using their ID.
     *
     * - Calls `GetSupplierInfoById` with the `id` from URL parameters.
     * - If the response is successful (`success`):
     *   - Updates the `user` state with the received data.
     *   - If the supplier has a `selfie`, updates `photoPicker` to show it as a preview.
     * - Calls `GetAllToContractor` to fetch the list of all contractors
     *   (can be used for dropdowns or validations).
     */
    const getSupplierInfo = () => {
        dispatch(GetSupplierInfoById(params?.id)).then(
            ({ payload: { data: { data, success } } }) => {
                if (success) {
                    const deviceId = data?.deviceId ?? null;
                    setExtraData(data?.extraData);
                    setUser((prevData) => ({
                        ...prevData,
                        ...data,
                    }));
                    setShowUnlinkBtn(!!deviceId);
                    if(data?.selfie)
                        setPhotoPicker({
                            ...PHOTO_PICKER_MODEL,
                            previewImage: `data:image/jpeg;base64,${data?.selfie}`,
                            preview: IconPersonDefault
                        });
                }
            }
        );
    }

    /**
     * Initializes state and fetches supplier information.
     *
     * - `useState` is used here with an initializer function that calls `getSupplierInfo()`
     *   to load supplier data when the component mounts.
     */
    useEffect(() => {
        getSupplierInfo();
        dispatch(GetHeaders());
    }, []);


    useEffect(() => {
            dispatch(GetUserRestriction()).then(
                ({
                    payload: {
                        data: { data, success },
                    },
                }) => {
                    if (success === true) {
                        setExtraDataEnabled(data?.extraDataSupplier);
                    }
                },
            );
    }, []);

    /**
     * - `useEffect` runs whenever `modified` changes:
     *  If `modified` is true, it calls `getSupplierInfo()` again to refresh the data.
     *  Then resets `modified` to false to prevent unnecessary repeated calls.
     */
    useEffect(() => {
        if(modified){
            getSupplierInfo();
            setModified(false);
        }
    }, [modified])

    /**
     * Resets the form fields to their initial state.
     */
    const resetForm = () => {
        setUser({...SUPPLIER_EMPLOYEE_DATA, supplierId : supplierId });
    };

    /**
     * Validates the form fields and triggers the supplier creation if valid.
     * Shows a warning toast if required fields are missing or invalid.
     */
    const FormHandler = () => {
        const hasInvalidField = !isValidDob || !isValidPhoneNumber || !isValidEmail || !isValidPassword;

        if (!user.isValidEmployee() || hasInvalidField) {
            toast.warn(t('please_fill_all_fields'));
            setEmptyFields(true);
            return;
        }

        setEmptyFields(false);
        updateSupplier();
    };

    /**
     * Dispatches the action to create a supplier employee using form data and image file.
     * Displays success or error feedback using toast notifications.
     */
    const updateSupplier = () => {
        let imageFile = getPhotoPicker(photoPicker);
        dispatch(UpdateSupplierEmployee(
                {
                    data: user.toRegister(user?.password, user?.extraData),
                    file: imageFile,
                    updateImage: user.selfie !== null,
                    setModified: setModified
                }
            )).then(({payload:{data:{data, success, code}}}) => {
                if (success) {
                    if(user?.password){
                        setSecretModal(true);
                        setSecretKey(data?.secret)
                    }
                    setEmptyFields(false);
                    toast.success(t('has_been_updated_successfully'));
                }else{
                    errorMsg(code)
                    setEmptyFields(true);
                }
            }
        );
    }

    /**
     * Handle to change step
     * @param step
     */
    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    /**
     * This function handles changes in the form fields for extra data. It updates the 'extraData' state by spreading the previous data
     * and updating the specific field (identified by 'name') with the new value from the form input.
     * @param e
     */
    const { handleChange } = HandleChange({
    model: extraData,
    setModel: setExtraData,
    });

    useEffect(() => {
        setUser((prev) => ({ ...prev, extraData }));
    }, [extraData]);

    return {
        user,
        setUser,
        companyName,
        FormHandler,
        resetForm,
        steps, setSteps,
        activeStep, setActiveStep,
        handleStepChange,
        showUnlinkBtn,
        setModified,
        handleChange,
        setExtraData,
        headersList,
        extraData,
        extraDataEnabled
    };
}
